import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronRight, Pencil, Tag, StickyNote, Check, X as XIcon, Trash2, Clock, RotateCcw } from "lucide-react";
import { toast } from "sonner";

// =====================================================================
// LineEditControls — drop-in chairside editor for a line item.
//
// Renders three inline affordances under (or beside) a line:
//   1. Price — click to edit. If `gross !== net`, the gross price is
//      shown struck-through and the net price bold.
//   2. Discount — "+ Discount" pill when none set, "X% off · reason"
//      pill when set. Click opens a small popover with %/₱ + reason.
//   3. Note — "+ Note" pill when empty, "📝 note text…" pill when set.
//      Click expands an inline textarea.
//
// All three callbacks are async and accept a single argument:
//   onPriceChange(grossCentavos)  → Promise
//   onDiscountChange({ pct, amount, reason })  → Promise
//   onNoteChange(note)  → Promise
// Pass null/undefined to a callback to disable that affordance.
//
// The component owns transient editor state (input values, open/closed
// popover) but never owns the canonical data — parents pass current
// values via props and rerender on save.
// =====================================================================

function fmt(centavos, currency) {
  const symbol = currency === "PHP" ? "₱"
              : currency === "USD" ? "$"
              : currency === "INR" ? "₹"
              : currency ? `${currency} ` : "";
  const n = Math.round((centavos ?? 0) / 100);
  return symbol + n.toLocaleString("en-US");
}

export function netPrice(grossCentavos, pct, amountCentavos) {
  const gross = Number(grossCentavos || 0);
  if (pct != null && Number(pct) > 0) {
    return Math.max(0, Math.round(gross * (1 - Number(pct) / 100)));
  }
  if (amountCentavos != null && Number(amountCentavos) > 0) {
    return Math.max(0, gross - Number(amountCentavos));
  }
  return gross;
}

// Build the standard preset list for a line. Parents pass relevant
// context (patient tag, surface count, procedure's bundle pct) and
// get back a curated array of one-click discount options.
//
// Output shape: array of { id, label, pct, amount, reason } where
// EXACTLY ONE of pct/amount is set per preset. The popover renders
// these in order; clicking one applies it and closes immediately.
export function buildLinePresets({
  patientSeniorPwdTag = null, // 'senior' | 'pwd' | null — drives ordering only
} = {}) {
  const out = [];
  // Senior + PWD always present (PH legal). If patient tagged, the
  // matching one is highlighted (ordered first).
  const senior = { id: "senior", label: "Senior 20%", pct: 20, reason: "Senior Citizen" };
  const pwd    = { id: "pwd",    label: "PWD 20%",    pct: 20, reason: "PWD" };
  if (patientSeniorPwdTag === "pwd")    out.push(pwd, senior);
  else                                  out.push(senior, pwd);

  // NOTE: no Multi-surface bundle preset. The clinic bundle policy
  // (procedures.additional_surface_pct) is now baked into the line's
  // price at charting time, so offering it again here would double-
  // discount. Surface bundling lives in the price, not in a discount.

  // Loyalty default — clinic-policy preset. Configurable later in
  // Settings → Procedures.
  out.push({ id: "loyalty", label: "Loyalty 10%", pct: 10, reason: "Loyalty" });

  return out;
}

export default function LineEditControls({
  grossCentavos,
  currency = "PHP",
  discountPct,
  discountAmountCentavos,
  discountReason,
  notes,
  chairMin,                 // current chair time in minutes (null/undefined hides chip)
  onChairMinChange,         // (mins:int|null) => Promise; null disables the chip
  presets,                  // optional [{ id, label, pct, amount, reason }, ...]
  onPriceChange,
  onDiscountChange,
  onNoteChange,
  onRemove,
  testIdPrefix = "line",
}) {
  const net = netPrice(grossCentavos, discountPct, discountAmountCentavos);
  const hasDiscount = (discountPct != null && Number(discountPct) > 0)
                   || (discountAmountCentavos != null && Number(discountAmountCentavos) > 0);

  return (
    <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid={`${testIdPrefix}-edit-controls`}>
      <PriceEditor
        grossCentavos={grossCentavos}
        netCentavos={net}
        currency={currency}
        canEdit={!!onPriceChange}
        onSave={onPriceChange}
        showStrike={hasDiscount && net !== grossCentavos}
        testIdPrefix={testIdPrefix}
      />
      {onDiscountChange ? (
        <DiscountControl
          discountPct={discountPct}
          discountAmountCentavos={discountAmountCentavos}
          discountReason={discountReason}
          grossCentavos={grossCentavos}
          currency={currency}
          presets={presets}
          onChange={onDiscountChange}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {onChairMinChange ? (
        <TimeControl
          chairMin={chairMin}
          onChange={onChairMinChange}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {onNoteChange ? (
        <NoteControl
          notes={notes}
          onChange={onNoteChange}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove line"
          data-testid={`${testIdPrefix}-remove`}
          className="ml-auto text-slate-400 hover:text-rose-600 p-0.5"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      ) : null}
    </div>
  );
}

// ---- Price editor ---------------------------------------------------
function PriceEditor({ grossCentavos, netCentavos, currency, canEdit, onSave, showStrike, testIdPrefix }) {
  const [editing, setEditing] = useState(false);
  const [draftPesos, setDraftPesos] = useState("");
  const [busy, setBusy] = useState(false);

  function open() {
    setDraftPesos(String(Math.round((grossCentavos ?? 0) / 100)));
    setEditing(true);
  }

  async function save() {
    if (busy) return;
    const pesos = Number(draftPesos);
    if (!Number.isFinite(pesos) || pesos < 0) {
      setEditing(false);
      return;
    }
    const cents = Math.round(pesos * 100);
    if (cents === Number(grossCentavos || 0)) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try { await onSave(cents); }
    finally { setBusy(false); setEditing(false); }
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-slate-400">{currency === "PHP" ? "₱" : ""}</span>
        <input
          type="number"
          min="0"
          step="1"
          autoFocus
          value={draftPesos}
          onChange={(e) => setDraftPesos(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter")  { e.preventDefault(); save(); }
            if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          data-testid={`${testIdPrefix}-price-input`}
          className="w-20 px-1.5 py-0.5 text-xs rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
        />
        <button type="button" onClick={save} disabled={busy} aria-label="Save price" data-testid={`${testIdPrefix}-price-save`} className="text-emerald-600 hover:bg-emerald-50 rounded p-0.5">
          <Check className="w-3 h-3" />
        </button>
        <button type="button" onClick={() => setEditing(false)} disabled={busy} aria-label="Cancel" className="text-slate-400 hover:bg-slate-100 rounded p-0.5">
          <XIcon className="w-3 h-3" />
        </button>
      </span>
    );
  }

  if (!canEdit) {
    return (
      <span className="inline-flex items-center gap-1 tabular-nums" data-testid={`${testIdPrefix}-price-display`}>
        {showStrike ? <span className="text-slate-400 line-through">{fmt(grossCentavos, currency)}</span> : null}
        <span className="font-semibold text-slate-700">{fmt(netCentavos, currency)}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={open}
      data-testid={`${testIdPrefix}-price-display`}
      title="Click to override price"
      className="inline-flex items-center gap-1 tabular-nums hover:bg-slate-50 rounded px-1 py-0.5 group"
    >
      {showStrike ? <span className="text-slate-400 line-through">{fmt(grossCentavos, currency)}</span> : null}
      <span className="font-semibold text-slate-700">{fmt(netCentavos, currency)}</span>
      <Pencil className="w-2.5 h-2.5 text-slate-300 group-hover:text-slate-500" />
    </button>
  );
}

// ---- Discount control ----------------------------------------------
function DiscountControl({ discountPct, discountAmountCentavos, discountReason, grossCentavos, currency, presets, onChange, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const has = (discountPct != null && Number(discountPct) > 0)
           || (discountAmountCentavos != null && Number(discountAmountCentavos) > 0);

  // Apply a discount + slam the popover shut + show an Undo toast
  // for 5 seconds. Used by both preset clicks and Custom apply.
  const applyAndClose = async (vals) => {
    const prev = {
      pct:    discountPct ?? null,
      amount: discountAmountCentavos ?? null,
      reason: discountReason ?? null,
    };
    setOpen(false);
    try {
      await onChange(vals);
      const offCents = vals.pct != null
        ? Math.round(Number(grossCentavos || 0) * Number(vals.pct) / 100)
        : Number(vals.amount || 0);
      toast.success(
        `Discount applied · −${fmt(offCents, currency)}`,
        {
          duration: 5000,
          action: {
            label: "Undo",
            onClick: async () => {
              await onChange(prev);
              toast.message("Discount reverted");
            },
          },
        },
      );
    } catch (e) {
      // toast already surfaced upstream from onChange's RPC layer
    }
  };

  let label = "Discount";
  if (has) {
    if (discountPct != null && Number(discountPct) > 0) {
      label = `${Number(discountPct).toFixed(Number(discountPct) % 1 === 0 ? 0 : 2)}% off`;
    } else if (discountAmountCentavos != null && Number(discountAmountCentavos) > 0) {
      label = `${fmt(discountAmountCentavos, currency)} off`;
    }
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={has ? `${testIdPrefix}-discount-pill` : `${testIdPrefix}-discount-add`}
        title={has ? `${label}${discountReason ? ` · ${discountReason}` : ""}` : "Add discount"}
        className={`text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
          has
            ? "bg-amber-50 text-amber-800 border-amber-200 font-semibold hover:opacity-80"
            : "border-dashed border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400 font-medium"
        }`}
      >
        <Tag className="w-3 h-3" />
        {has ? label : "Discount"}
      </button>

      {open ? (
        <DiscountPopover
          anchorRef={anchorRef}
          initialPct={discountPct ?? null}
          initialAmount={discountAmountCentavos ?? null}
          initialReason={discountReason ?? ""}
          grossCentavos={grossCentavos}
          currency={currency}
          presets={presets}
          onClose={() => setOpen(false)}
          onApply={applyAndClose}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
    </>
  );
}

function DiscountPopover({ anchorRef, initialPct, initialAmount, initialReason, grossCentavos, currency, presets = [], onClose, onApply, testIdPrefix }) {
  const [mode, setMode]     = useState(initialAmount != null ? "amount" : "pct");
  const [pct, setPct]       = useState(initialPct != null ? String(initialPct) : "");
  const [amount, setAmount] = useState(initialAmount != null ? String(Math.round(initialAmount / 100)) : "");
  const [reason, setReason] = useState(initialReason || "");
  const [busy, setBusy]     = useState(false);
  const popRef              = useRef(null);
  const [pos, setPos]       = useState({ top: 0, left: 0 });
  const hasExistingDiscount = (initialPct != null && initialPct > 0)
                           || (initialAmount != null && initialAmount > 0);

  // Position the popover under the anchor button; flip above if it
  // would clip the bottom of the viewport.
  useLayoutEffect(() => {
    function place() {
      const anchor = anchorRef?.current;
      const pop    = popRef.current;
      if (!anchor || !pop) return;
      const a = anchor.getBoundingClientRect();
      const popHeight = pop.offsetHeight || 320;
      const popWidth  = pop.offsetWidth  || 288;
      const margin    = 4;
      const wantsAbove = (a.bottom + margin + popHeight) > window.innerHeight
                      && (a.top - margin - popHeight) > 0;
      const top  = wantsAbove ? a.top - margin - popHeight : a.bottom + margin;
      // Keep the popover inside the viewport horizontally.
      let left = a.left;
      if (left + popWidth + 8 > window.innerWidth) left = window.innerWidth - popWidth - 8;
      if (left < 8) left = 8;
      setPos({ top, left });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef]);

  // Close on outside click / Escape.
  useEffect(() => {
    function onDoc(e) {
      const pop = popRef.current;
      const anchor = anchorRef?.current;
      if (pop && pop.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      onClose();
    }
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  const customOff = (() => {
    if (mode === "pct") {
      const n = Number(pct);
      if (!Number.isFinite(n) || n <= 0) return null;
      return Math.round(Number(grossCentavos || 0) * Math.min(100, n) / 100);
    }
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.min(Number(grossCentavos || 0), Math.round(n * 100));
  })();
  const customNet = customOff == null ? null : Math.max(0, Number(grossCentavos || 0) - customOff);
  const customValid = customOff != null && reason.trim().length > 0;

  async function applyCustom() {
    if (busy || !customValid) return;
    setBusy(true);
    try {
      if (mode === "pct") {
        await onApply({ pct: Number(pct), amount: null, reason: reason.trim() });
      } else {
        await onApply({ pct: null, amount: Math.round(Number(amount) * 100), reason: reason.trim() });
      }
    } finally { setBusy(false); }
  }

  async function applyPreset(p) {
    if (busy) return;
    setBusy(true);
    try {
      await onApply({
        pct:    p.pct ?? null,
        amount: p.amount ?? null,
        reason: p.reason || p.label,
      });
    } finally { setBusy(false); }
  }

  async function clear() {
    if (busy) return;
    setBusy(true);
    try { await onApply({ pct: null, amount: null, reason: null }); }
    finally { setBusy(false); }
  }

  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : "";

  const popover = (
    <div
      ref={popRef}
      role="dialog"
      data-testid={`${testIdPrefix}-discount-popover`}
      style={{ position: "fixed", top: pos.top, left: pos.left }}
      className="z-50 bg-white rounded-xl shadow-xl border border-slate-200 w-80"
    >
      {/* Header — gross anchor */}
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-baseline gap-2">
          <span className="text-[11px] uppercase tracking-wide text-slate-400">Gross</span>
          <span className="text-sm font-semibold text-slate-900 tabular-nums">{fmt(grossCentavos, currency)}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-slate-400 hover:text-slate-700 p-0.5"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Quick presets — one click = apply + close + Undo toast */}
      {presets && presets.length > 0 ? (
        <ul className="px-2 py-2 space-y-1" data-testid={`${testIdPrefix}-discount-presets`}>
          {presets.map((p) => {
            const off = p.pct != null
              ? Math.round(Number(grossCentavos || 0) * Number(p.pct) / 100)
              : Number(p.amount || 0);
            const net = Math.max(0, Number(grossCentavos || 0) - off);
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => applyPreset(p)}
                  disabled={busy}
                  data-testid={`${testIdPrefix}-discount-preset-${p.id}`}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left rounded-lg border border-slate-200 bg-white hover:border-teal-400 hover:bg-teal-50/40 disabled:opacity-50 transition"
                >
                  <span className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-slate-900 truncate">{p.label}</span>
                    <span className="text-[11px] text-slate-500 tabular-nums">
                      −{fmt(off, currency)} · Net <span className="font-semibold text-slate-700">{fmt(net, currency)}</span>
                    </span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* Custom amount — when none of the presets fit */}
      <div className="px-4 pt-1 pb-3 border-t border-slate-100 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-slate-400">Custom</span>
          <div className="inline-flex bg-slate-100 rounded-md p-0.5 text-[11px] font-semibold">
            <button type="button" onClick={() => setMode("pct")} data-testid={`${testIdPrefix}-discount-mode-pct`} className={`px-2 py-0.5 rounded ${mode === "pct" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>%</button>
            <button type="button" onClick={() => setMode("amount")} data-testid={`${testIdPrefix}-discount-mode-amount`} className={`px-2 py-0.5 rounded ${mode === "amount" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{symbol}</button>
          </div>
        </div>

        {mode === "pct" ? (
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && customValid) applyCustom(); }}
              placeholder="20"
              data-testid={`${testIdPrefix}-discount-pct-input`}
              className="w-full pr-8 pl-2.5 py-1.5 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
          </div>
        ) : (
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">{symbol}</span>
            <input
              type="number"
              min="0"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && customValid) applyCustom(); }}
              placeholder="500"
              data-testid={`${testIdPrefix}-discount-amount-input`}
              className="w-full pl-7 pr-2.5 py-1.5 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
            />
          </div>
        )}

        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && customValid) applyCustom(); }}
          placeholder="Reason (required)"
          data-testid={`${testIdPrefix}-discount-reason-input`}
          className="w-full px-2.5 py-1.5 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
        />

        {customNet != null ? (
          <div className="flex items-center justify-between text-xs px-1 pt-0.5" data-testid={`${testIdPrefix}-discount-preview`}>
            <span className="text-slate-500">Preview · Net</span>
            <span className="font-semibold tabular-nums text-slate-900">{fmt(customNet, currency)}</span>
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-2 pt-1">
          {hasExistingDiscount ? (
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              data-testid={`${testIdPrefix}-discount-clear`}
              className="text-[11px] font-medium text-rose-600 hover:underline disabled:opacity-50"
            >
              Remove
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={applyCustom}
            disabled={busy || !customValid}
            data-testid={`${testIdPrefix}-discount-save`}
            className="px-3 py-1.5 text-xs font-semibold rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            {busy
              ? "Saving…"
              : (customOff != null
                ? `Apply −${fmt(customOff, currency)}`
                : "Apply")}
          </button>
        </div>
        <p className="text-[10px] text-slate-400 text-right -mt-0.5">↵ Apply · Esc Cancel</p>
      </div>
    </div>
  );

  return createPortal(popover, document.body);
}

// ---- Note control ---------------------------------------------------
function NoteControl({ notes, onChange, testIdPrefix }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(notes ?? "");
  const [busy, setBusy]       = useState(false);

  useEffect(() => { setDraft(notes ?? ""); }, [notes]);

  async function save() {
    if (busy) return;
    const next = draft.trim();
    if (next === (notes ?? "")) { setEditing(false); return; }
    setBusy(true);
    try { await onChange(next || null); }
    finally { setBusy(false); setEditing(false); }
  }

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1 w-full max-w-md">
        <input
          type="text"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter")  { e.preventDefault(); save(); }
            if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          placeholder="Per-line note…"
          data-testid={`${testIdPrefix}-note-input`}
          className="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
        />
        <button type="button" onClick={save} disabled={busy} aria-label="Save note" data-testid={`${testIdPrefix}-note-save`} className="text-emerald-600 hover:bg-emerald-50 rounded p-0.5">
          <Check className="w-3 h-3" />
        </button>
        <button type="button" onClick={() => setEditing(false)} disabled={busy} aria-label="Cancel" className="text-slate-400 hover:bg-slate-100 rounded p-0.5">
          <XIcon className="w-3 h-3" />
        </button>
      </div>
    );
  }

  if (!notes) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={`${testIdPrefix}-note-add`}
        className="text-[11px] font-medium px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400 inline-flex items-center gap-1"
      >
        <StickyNote className="w-3 h-3" /> Note
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={`${testIdPrefix}-note-pill`}
      title={notes}
      className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 max-w-[260px]"
    >
      <StickyNote className="w-3 h-3 shrink-0" />
      <span className="truncate">{notes}</span>
    </button>
  );
}

// ---- Time editor (per-line chair time override) --------------------
// Shows "N min" pill; click expands a tiny number input. Saving with an
// empty value clears the override (parent decides whether that means
// "back to auto" or "0 min" — current backend treats empty as "leave
// alone" and zero as "leave 0").
//
// Exported as a named export so it can be rendered standalone next to
// the per-line price (the Tx Plan Builder twins the two metrics on the
// right side of the line; the LineEditControls bottom-row variant is
// the legacy chip-row layout).
export function TimeControl({ chairMin, onChange, testIdPrefix, title }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(chairMin == null ? "" : String(chairMin));
  const [busy, setBusy]       = useState(false);

  useEffect(() => { setDraft(chairMin == null ? "" : String(chairMin)); }, [chairMin]);

  async function save() {
    if (busy) return;
    const trimmed = draft.trim();
    const next = trimmed === "" ? null : Math.max(0, Math.round(Number(trimmed)));
    if (Number.isNaN(next)) { setEditing(false); return; }
    if (next === (chairMin ?? null)) { setEditing(false); return; }
    setBusy(true);
    try { await onChange(next); }
    finally { setBusy(false); setEditing(false); }
  }

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1">
        <Clock className="w-3 h-3 text-slate-500 shrink-0" />
        <input
          type="number"
          min="0"
          max="600"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter")  { e.preventDefault(); save(); }
            if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          placeholder="min"
          data-testid={`${testIdPrefix}-time-input`}
          className="w-16 px-2 py-1 text-xs rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
        />
        <span className="text-[10px] text-slate-500">min</span>
        <button type="button" onClick={save} disabled={busy} aria-label="Save time" data-testid={`${testIdPrefix}-time-save`} className="text-emerald-600 hover:bg-emerald-50 rounded p-0.5">
          <Check className="w-3 h-3" />
        </button>
        <button type="button" onClick={() => setEditing(false)} disabled={busy} aria-label="Cancel" className="text-slate-400 hover:bg-slate-100 rounded p-0.5">
          <XIcon className="w-3 h-3" />
        </button>
      </div>
    );
  }

  if (chairMin == null) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={`${testIdPrefix}-time-add`}
        title={title || undefined}
        className="text-[11px] font-medium px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400 inline-flex items-center gap-1"
      >
        <Clock className="w-3 h-3" /> Time
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={`${testIdPrefix}-time-pill`}
      title={title || "Override chair time for this item"}
      className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
    >
      <Clock className="w-3 h-3 shrink-0" />
      <span>{chairMin} min</span>
    </button>
  );
}
